import log from "logbeacon";
import { HeartbeatConfig } from "./types";

/**
 * 心跳管理器
 * 负责维持 WebSocket 连接的活跃状态
 */
export class HeartbeatManager {
  private config: Required<HeartbeatConfig>;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private timeoutTimer: NodeJS.Timeout | null = null;
  private lastPongTime: number = 0;
  private isEnabled: boolean = false;
  private sendCallback: ((message: string) => void) | null = null;
  private timeoutCallback: (() => void) | null = null;

  constructor(config: Partial<HeartbeatConfig>) {
    // 设置默认配置
    this.config = {
      enabled: config.enabled ?? false,
      interval: config.interval ?? 30000, // 默认 30 秒
      timeout: config.timeout ?? 10000, // 默认 10 秒超时
      message: config.message ?? "ping",
      expectResponse: config.expectResponse ?? false,
      responseValidator: config.responseValidator ?? (() => true),
    };
  }

  /**
   * 启动心跳
   */
  start(
    sendCallback: (message: string) => void,
    timeoutCallback: () => void,
  ): void {
    if (!this.config.enabled) {
      log.debug("[HeartbeatManager] 心跳未启用");
      return;
    }

    this.sendCallback = sendCallback;
    this.timeoutCallback = timeoutCallback;
    this.isEnabled = true;
    this.lastPongTime = Date.now();

    log.debug(
      `[HeartbeatManager] 启动心跳,间隔: ${this.config.interval}ms, 超时: ${this.config.timeout}ms`,
    );

    this.scheduleNextHeartbeat();
  }

  /**
   * 停止心跳
   */
  stop(): void {
    log.debug("[HeartbeatManager] 停止心跳");
    this.isEnabled = false;
    this.clearTimers();
    this.sendCallback = null;
    this.timeoutCallback = null;
  }

  /**
   * 重置心跳计时器
   * 当收到任何消息时调用,表示连接活跃
   */
  reset(): void {
    if (!this.isEnabled) {
      return;
    }

    this.lastPongTime = Date.now();
    this.clearTimeoutTimer();

    // 重新调度下一次心跳
    this.clearHeartbeatTimer();
    this.scheduleNextHeartbeat();
  }

  /**
   * 处理服务端的心跳响应
   */
  handleResponse(data: any): boolean {
    if (!this.config.expectResponse) {
      return false;
    }

    const isValid = this.config.responseValidator(data);
    if (isValid) {
      log.debug("[HeartbeatManager] 收到有效的心跳响应");
      this.reset();
      return true;
    }

    return false;
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<HeartbeatConfig>): void {
    const wasEnabled = this.config.enabled;

    this.config = {
      ...this.config,
      ...config,
    };

    // 如果启用状态改变,重新启动或停止
    if (wasEnabled !== this.config.enabled) {
      if (this.config.enabled && this.sendCallback && this.timeoutCallback) {
        this.start(this.sendCallback, this.timeoutCallback);
      } else {
        this.stop();
      }
    }
  }

  /**
   * 获取当前配置
   */
  getConfig(): Required<HeartbeatConfig> {
    return { ...this.config };
  }

  /**
   * 检查心跳是否启用
   */
  isHeartbeatEnabled(): boolean {
    return this.isEnabled && this.config.enabled;
  }

  /**
   * 获取距离上次响应的时间
   */
  getTimeSinceLastPong(): number {
    return Date.now() - this.lastPongTime;
  }

  /**
   * 调度下一次心跳
   */
  private scheduleNextHeartbeat(): void {
    if (!this.isEnabled) {
      return;
    }

    this.clearHeartbeatTimer();

    this.heartbeatTimer = setTimeout(() => {
      this.sendHeartbeat();
    }, this.config.interval);
  }

  /**
   * 发送心跳消息
   */
  private sendHeartbeat(): void {
    if (!this.isEnabled || !this.sendCallback) {
      return;
    }

    const message =
      typeof this.config.message === "function"
        ? this.config.message()
        : this.config.message;

    log.debug(`[HeartbeatManager] 发送心跳: ${message}`);

    try {
      this.sendCallback(message);

      // 如果期望响应,启动超时计时器
      if (this.config.expectResponse) {
        this.startTimeoutTimer();
      } else {
        // 不期望响应,直接调度下一次心跳
        this.scheduleNextHeartbeat();
      }
    } catch (error) {
      log.error("[HeartbeatManager] 发送心跳失败:", error);
      this.handleTimeout();
    }
  }

  /**
   * 启动超时计时器
   */
  private startTimeoutTimer(): void {
    this.clearTimeoutTimer();

    this.timeoutTimer = setTimeout(() => {
      log.warn("[HeartbeatManager] 心跳超时");
      this.handleTimeout();
    }, this.config.timeout);
  }

  /**
   * 处理心跳超时
   */
  private handleTimeout(): void {
    if (this.timeoutCallback) {
      this.timeoutCallback();
    }
  }

  /**
   * 清除心跳计时器
   */
  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 清除超时计时器
   */
  private clearTimeoutTimer(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /**
   * 清除所有计时器
   */
  private clearTimers(): void {
    this.clearHeartbeatTimer();
    this.clearTimeoutTimer();
  }
}
